// src/utils/pushNotifications.js
// Helpers para gerenciar a inscrição em Web Push do navegador atual.
//
// Existem dois conjuntos de rotas equivalentes no backend:
//   /portal/push/*  → para o Portal do Condômino
//   /push/*         → para usuários administrativos (Admin/Gerente/Sindico)
// Como ambos respondem exatamente igual, basta escolher o prefixo correto
// passando { admin:true } nas funções ativar/desativar/testar.
import api from "../services/api";

function _basePath(opts = {}) {
  if (opts.portaria) return "/portaria/push";
  return opts.admin ? "/push" : "/portal/push";
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSuportado() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager"  in window
    && "Notification" in window;
}

export function permissaoAtual() {
  if (!pushSuportado()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function obterRegistration() {
  if (!pushSuportado()) return null;
  // Aguarda o SW estar pronto (já registrado por registerServiceWorker)
  return await navigator.serviceWorker.ready;
}

/**
 * Retorna o status real para o usuário atual.
 *
 * IMPORTANTE: a Push API do navegador é por origem (todos os usuários que
 * logarem no mesmo browser veem a MESMA subscription do PushManager). Já no
 * backend cada subscription pertence a um único idUsuario. Por isso, "ter
 * subscription no browser" não significa "estar inscrito como este usuário".
 * Consultamos o backend (`/push/status?endpoint=...`) para distinguir.
 */
export async function statusAssinaturaAtual(opts = {}) {
  if (!pushSuportado()) return { suportado: false, assinado: false };
  if (Notification.permission !== "granted") {
    return { suportado: true, assinado: false, permissao: Notification.permission };
  }
  try {
    const reg = await obterRegistration();
    const sub = await reg?.pushManager.getSubscription();
    const endpoint = sub?.endpoint || null;

    if (!endpoint) return { suportado: true, assinado: false, permissao: "granted" };

    // Confere com o backend se essa subscription está vinculada ao USUÁRIO atual.
    let assinadoServidor = false;
    try {
      const base = _basePath(opts);
      const r = await api("GET", `${base}/status?endpoint=${encodeURIComponent(endpoint)}`);
      assinadoServidor = !!r?.data?.assinado;
    } catch { /* mantém false */ }

    return {
      suportado: true,
      assinado:  assinadoServidor,
      endpoint,
      permissao: "granted",
    };
  } catch {
    return { suportado: true, assinado: false, permissao: "granted" };
  }
}

/**
 * Verifica se o backend tem Web Push (VAPID) configurado.
 * Retorna { enabled:boolean, publicKey?:string|null }.
 * Útil para a UI mostrar mensagem clara quando o servidor está sem VAPID,
 * em vez de o usuário só ver "Falha ao ativar" sem entender o motivo.
 */
export async function servidorHabilitado(opts = {}) {
  const base = _basePath(opts);
  try {
    const r = await api("GET", `${base}/public-key`);
    return {
      enabled  : !!(r?.data?.enabled && r?.data?.publicKey),
      publicKey: r?.data?.publicKey || null,
    };
  } catch {
    return { enabled: false, publicKey: null };
  }
}

export async function ativarPush(opts = {}) {
  if (!pushSuportado()) throw new Error("Push notifications não suportadas neste navegador.");

  const base = _basePath(opts);

  // 1) Pede permissão
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    throw new Error(perm === "denied"
      ? "Você bloqueou as notificações. Habilite nas configurações do navegador."
      : "Permissão não concedida.");
  }

  // 2) Pega a public key do backend
  const r = await api("GET", `${base}/public-key`);
  if (!r?.data?.enabled || !r.data.publicKey) {
    throw new Error("Push não está configurado no servidor.");
  }
  const applicationServerKey = urlBase64ToUint8Array(r.data.publicKey);

  // 3) Subscribe no navegador
  const reg = await obterRegistration();
  if (!reg) throw new Error("Service Worker indisponível.");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  }

  // 4) Envia subscription para o backend
  await api("POST", `${base}/subscribe`, { subscription: sub.toJSON() });
  return { endpoint: sub.endpoint };
}

export async function desativarPush(opts = {}) {
  if (!pushSuportado()) return;
  const base = _basePath(opts);
  const reg = await obterRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    const endpoint = sub.endpoint;
    try { await sub.unsubscribe(); } catch {}
    try { await api("POST", `${base}/unsubscribe`, { endpoint }); } catch {}
  }
}

export async function testarPush(opts = {}) {
  const base = _basePath(opts);
  return await api("POST", `${base}/test`);
}

// ── Beep de notificação (sem assets) — gera tom via WebAudio ──────────────
let _audioCtx = null;
export function tocarBeepNotificacao() {
  try {
    if (typeof window === "undefined" || !window.AudioContext && !window.webkitAudioContext) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!_audioCtx) _audioCtx = new Ctx();
    // Em alguns navegadores o contexto inicia suspenso até primeira interação
    if (_audioCtx.state === "suspended") _audioCtx.resume().catch(()=>{});

    const now = _audioCtx.currentTime;
    const osc = _audioCtx.createOscillator();
    const gain = _audioCtx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, now);             // Lá agudo
    osc.frequency.setValueAtTime(1320, now + 0.12);     // sobe pra Mi
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
    osc.connect(gain).connect(_audioCtx.destination);
    osc.start(now);
    osc.stop(now + 0.3);
  } catch { /* silencioso */ }
}
